import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Switch, Table, Tag } from 'antd';
import { AccessService } from '../../service/api/access-service';
import { usePermission } from '../../core/utils/PermissionContext';

const formatDate = v => (v ? new Date(v.endsWith('Z') ? v : `${v}Z`).toLocaleString() : '—');

const RolesTab = ({ canCreate, canEdit, onOpenMatrix }) => {
  const { isSuperAdmin, reload: reloadMyAccess } = usePermission();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // role object or {} for new
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(await AccessService.getRoles());
    } catch (_e) {
      // toast shown by httpInterceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = role => {
    setEditing(role || {});
    form.setFieldsValue({
      entraRoleValue: role?.entraRoleValue || '',
      name: role?.name || '',
      description: role?.description || '',
      isSuperAdmin: !!role?.isSuperAdmin,
      isActive: role ? !!role.isActive : true,
    });
  };

  const save = async () => {
    const values = await form.validateFields();
    const payload = { ...values, entraRoleValue: values.entraRoleValue.trim(), id: editing?.id, rowVer: editing?.rowVer };

    const doSave = async () => {
      setSaving(true);
      try {
        await AccessService.saveRole(payload);
        setEditing(null);
        await load();
        reloadMyAccess();
      } catch (_e) {
        // toast shown by httpInterceptor (409 conflict, 422 last super-admin, 403 ...)
      } finally {
        setSaving(false);
      }
    };

    const superChanged = !!payload.isSuperAdmin !== !!editing?.isSuperAdmin;
    if (superChanged) {
      Modal.confirm({
        title: payload.isSuperAdmin ? 'Grant super-admin?' : 'Remove super-admin?',
        content: payload.isSuperAdmin
          ? 'Users with this Entra role will bypass every application permission (not data scope). This is audited.'
          : 'Users with this Entra role will lose the super-admin bypass.',
        okText: 'Yes, save',
        okButtonProps: { danger: true },
        onOk: doSave,
      });
    } else {
      await doSave();
    }
  };

  const columns = [
    { title: 'Role', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    {
      title: 'Entra role value',
      dataIndex: 'entraRoleValue',
      render: v => <code>{v}</code>,
      sorter: (a, b) => a.entraRoleValue.localeCompare(b.entraRoleValue),
      defaultSortOrder: 'ascend',
    },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    {
      title: 'Status',
      render: (_, r) => (
        <>
          {r.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
          {r.isSuperAdmin && <Tag color="red">Super-admin</Tag>}
        </>
      ),
    },
    { title: 'Grants', dataIndex: 'grantCount', width: 80, sorter: (a, b) => a.grantCount - b.grantCount },
    { title: 'Last updated', render: (_, r) => `${formatDate(r.updatedOnUtc)}${r.updatedBy ? ` · ${r.updatedBy}` : ''}` },
    {
      title: 'Actions',
      width: 210,
      render: (_, r) => (
        <div className="d-flex gap-2">
          {onOpenMatrix && (
            <Button size="small" onClick={() => onOpenMatrix(r.id)}>
              Permissions
            </Button>
          )}
          {canEdit && (
            <Button size="small" onClick={() => openEditor(r)} disabled={r.isSuperAdmin && !isSuperAdmin}>
              Edit
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="Who has a role is managed in Microsoft Entra (Enterprise application → Users and groups). This screen only decides what each role can do in this application."
      />
      <div className="ac-toolbar">
        <Button onClick={load} loading={loading}>
          Refresh
        </Button>
        <span className="ac-spacer" />
        {canCreate && (
          <Button type="primary" onClick={() => openEditor(null)}>
            Add role mapping
          </Button>
        )}
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={roles} pagination={{ pageSize: 50 }} scroll={{ x: 900 }} />

      <Modal
        open={!!editing}
        title={editing?.id ? 'Edit role mapping' : 'Add role mapping'}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="entraRoleValue"
            label="Entra App Role value"
            extra="Must match the 'roles' claim exactly, including case (e.g. WMS_Manager)."
            rules={[
              { required: true, message: 'Entra role value is required' },
              { max: 120 },
              { pattern: /^\S+$/, message: 'No spaces allowed' },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="name" label="Display name" rules={[{ required: true }, { max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="isSuperAdmin"
            label="Super-admin (bypasses all application permissions)"
            valuePropName="checked"
            extra={!isSuperAdmin ? 'Only a super-admin can change this.' : undefined}
          >
            <Switch disabled={!isSuperAdmin} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RolesTab;
